import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    Paper,
    CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { useState, useEffect } from 'react';
import { CuffaAPI } from 'api/cuffa/cuffa.api';


interface IDadosFamilia {
    nomeUnidadeFamiliar: string;
    nomeComunidade: string;
    nomeInformante: string;
    cpf: string;
    dataNascimento: string;
    genero: string;
    telefone: string;
    estadoCivil: string;
    nomeConjuge: string;
    dataNascimentoConjuge: string;
    generoConjuge: string;
    qtdPessoas: string;
}

export const DadosFamiliaVisualizar: React.FC = () => {
    const navigate = useNavigate();
    const { uuid } = useParams<{ uuid: string }>();

    const [dados, setDados] = useState<IDadosFamilia | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFamilyData = async () => {
            if (!uuid) {
                setLoading(false);
                return;
            }
            try {
                // A "response" aqui é o objeto { Status: 'OK', dados: { ... } }
                const response = await CuffaAPI.getDataByID(uuid);

                // Verifique se a resposta e a propriedade "dados" existem
                if (response && response.dados) {
                    // Use "response.dados" para acessar os campos da família
                    const apiData = response.dados;

                    const mappedData: IDadosFamilia = {
                        nomeUnidadeFamiliar: apiData.family_unit_name,
                        nomeComunidade: apiData.community_name,
                        nomeInformante: apiData.informant_full_name,
                        cpf: apiData.informant_cpf,
                        dataNascimento: apiData.informant_birth_date,
                        genero: apiData.informant_gender,
                        telefone: apiData.informant_phone,
                        estadoCivil: apiData.informant_marital_status,
                        nomeConjuge: apiData.spouse_full_name,
                        dataNascimentoConjuge: apiData.spouse_birth_date,
                        generoConjuge: apiData.spouse_gender,
                        qtdPessoas: String(apiData.family_members_count)
                    };
                    setDados(mappedData);
                } else {
                    console.error("Dados da família não encontrados na resposta da API:", response);
                    setDados(null);
                }
            } catch (error) {
                console.error("Erro ao buscar dados da família:", error);
                setDados(null);
            } finally {
                setLoading(false);
            }
        };

        fetchFamilyData();
    }, [uuid]);

    if (loading) {
        return (
            <MenuProvider>
                <TopNavBar />
                <Header title="Dados da Família" showBackButton />
                <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
                </Box>
            </MenuProvider>
        );
    }

    if (!dados) {
        return (
            <MenuProvider>
                <TopNavBar />
                <Header title="Dados da Família" showBackButton />
                <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
                    <Typography>Não foi possível carregar os dados da família. Verifique o console para mais detalhes.</Typography>
                </Box>
            </MenuProvider>
        );
    }

    // O restante do seu código JSX continua igual...
    const estadoCivilComConjuge = ['CASADO', 'CASADA(O)', 'UNIÃO ESTÁVEL']; // Adicionado 'CASADO' da sua API
    const temConjuge = estadoCivilComConjuge.includes(dados.estadoCivil?.toUpperCase());

    return (
        <MenuProvider>
            <TopNavBar />
            <Header title="Visualização dos Dados da Família" showBackButton />
            <Box width="100%" display="flex" justifyContent="center" margin="2rem">
                <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
                    <ContainerForm
                        title="Dados da Família"
                        subtitle="Visualização dos dados cadastrados"
                        width="100%"
                    >
                        {/* Seção: Identificação da Família */}
                        <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Identificação da Família
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemText
                                        primary="Nome da Unidade Familiar"
                                        secondary={dados.nomeUnidadeFamiliar || 'Não informado'}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary="Nome da Comunidade/Assentamento"
                                        secondary={dados.nomeComunidade || 'Não informado'}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary="Quantidade de pessoas na família"
                                        secondary={dados.qtdPessoas || 'Não informado'}
                                    />
                                </ListItem>
                            </List>
                        </Paper>

                        {/* Seção: Informante */}
                        <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Dados do Informante
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemText
                                        primary="Nome completo"
                                        secondary={dados.nomeInformante || 'Não informado'}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary="CPF"
                                        secondary={dados.cpf || 'Não informado'}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary="Data de nascimento"
                                        secondary={dados.dataNascimento || 'Não informada'}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary="Gênero"
                                        secondary={dados.genero || 'Não informado'}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary="Telefone para contato"
                                        secondary={dados.telefone || 'Não informado'}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary="Estado civil"
                                        secondary={dados.estadoCivil || 'Não informado'}
                                    />
                                </ListItem>
                            </List>
                        </Paper>

                        {/* Seção: Cônjuge (se aplicável) */}
                        {temConjuge && (
                            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Dados do(a) Cônjuge
                                </Typography>
                                <List>
                                    <ListItem>
                                        <ListItemText
                                            primary="Nome completo"
                                            secondary={dados.nomeConjuge || 'Não informado'}
                                        />
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <ListItemText
                                            primary="Data de nascimento"
                                            secondary={dados.dataNascimentoConjuge || 'Não informada'}
                                        />
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <ListItemText
                                            primary="Gênero"
                                            secondary={dados.generoConjuge || 'Não informado'}
                                        />
                                    </ListItem>
                                </List>
                            </Paper>
                        )}

                        <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => navigate(-1)}
                            >
                                Voltar
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    if (uuid) {
                                        navigate(ROUTES.VISITAS_INSTITUICOES_CUFFA_VISUALIZAR.replace(':uuid', uuid));
                                    }
                                }}
                            >
                                Avançar
                            </Button>
                        </Box>
                    </ContainerForm>
                </Box>
            </Box>
        </MenuProvider>
    );
};

export default DadosFamiliaVisualizar;